import {
    PreApprovalDto,
    PreApprovalStatus,
} from '@servicetitan/financing-api-client/src/prequals-api';

/** True when the provider name is GreenSky (case-insensitive). */
export const isGreenSkyProvider = (name?: string | null): boolean =>
    name?.toUpperCase() === 'GREENSKY';

/** The pre-approval's `General` FCRA prescreen disclosure body, or '' when absent. */
export const getGeneralDisclosureText = (preApproval?: PreApprovalDto | null): string =>
    preApproval?.disclosures?.find(d => d.category === 'General')?.text ?? '';

/**
 * A pre-approval is renderable (and therefore a ranking candidate) only when it is a GreenSky,
 * Approved pre-approval that carries both an amount and its FCRA `General` disclosure. Anything
 * else is treated as absent: no render, no candidate date, no suppression.
 */
export const isRenderableGreenSkyPreApproval = (preApproval?: PreApprovalDto | null): boolean =>
    !!preApproval &&
    isGreenSkyProvider(preApproval.providerName) &&
    preApproval.status === PreApprovalStatus.Approved &&
    preApproval.amount != null &&
    getGeneralDisclosureText(preApproval) !== '';

/** True when the provider name is Synchrony (case-insensitive). */
export const isSynchronyProvider = (name?: string | null): boolean =>
    name?.toUpperCase() === 'SYNCHRONY';

/**
 * A Synchrony pre-approval is renderable (and therefore a ranking candidate) only when it is a
 * Synchrony, Approved pre-approval that carries an amount. Unlike GreenSky it does not require an
 * FCRA `General` disclosure. Anything else is treated as absent: no render, no candidate date.
 */
export const isRenderableSynchronyPreApproval = (preApproval?: PreApprovalDto | null): boolean =>
    !!preApproval &&
    isSynchronyProvider(preApproval.providerName) &&
    preApproval.status === PreApprovalStatus.Approved &&
    preApproval.amount != null;

/**
 * True when the current provider shows pre-approval as a banner (not a plan card) and has a renderable
 * offer. GreenSky is excluded — it uses a plan card via `usePreApprovalDisplay`. Currently Synchrony-only.
 */
export const isRenderableBannerPreApproval = (
    provider: string | null | undefined,
    preApproval?: PreApprovalDto | null,
): boolean => {
    if (isSynchronyProvider(provider)) {
        return isRenderableSynchronyPreApproval(preApproval);
    }
    return false;
};

/** Minimal shape of setup data needed to decide the pre-approval submission path. */
export interface ActivePreApprovalSubmissionSetupData {
    provider?: string | null;
    prequalificationId?: string | null;
    loanId?: string | null;
    preApprovalId?: string | null;
}

/**
 * True when setup data describes an active GreenSky pre-approval credit application: a
 * `preApprovalId` is set, no prequalification or existing loan is in play, and the currently
 * selected provider is GreenSky. Provider-aware so a stale `preApprovalId` (left after a provider
 * switch or workflow reset) does not force the financing-direct submission/refresh path for a
 * non-pre-approval flow. Symmetric with `usePreApprovalDisplay`'s render gate (which also excludes
 * `prequalificationId`/`loanId`).
 */
export const isActivePreApprovalSubmission = (
    setupData?: ActivePreApprovalSubmissionSetupData | null,
): boolean =>
    !!setupData?.preApprovalId &&
    !setupData.prequalificationId &&
    !setupData.loanId &&
    isGreenSkyProvider(setupData.provider);
